/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Autocomplete from "@mui/material/Autocomplete";
import FileSaver from "file-saver";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import { API_HOHOLOK } from "utils";

function PerusahaanList() {
  const [menu, setMenu] = useState(null);
  const [perusahaan, setPerusahaan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({
    nama_perusahaan: "",
    jenis_perusahaan: "",
    alamat: "",
    email: "",
    nomor_telepon: "",
    npwp: "",
    sertifikat: "",
  });
  const [editId, setEditId] = useState(null);
  const [filterJenis, setFilterJenis] = useState("All");
  const [search, setSearch] = useState("");
  const jenisOptions = [
    "Kontraktor",
    "Konsultan Perencana",
    "Konsultan Pengawas",
  ];

  const fetchPerusahaan = () => {
    setLoading(true);
    axios
      .get(`${API_HOHOLOK}perusahaan`)
      .then((res) => {
        setPerusahaan(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchPerusahaan();
  }, []);

  // Definisikan kolom DataTable
  const columns = [
    { Header: "Nama Perusahaan", accessor: "nama_perusahaan" },
    { Header: "Jenis", accessor: "jenis_perusahaan" },
    { Header: "Alamat", accessor: "alamat" },
    { Header: "Email", accessor: "email" },
    { Header: "Telepon", accessor: "nomor_telepon" },
    { Header: "NPWP", accessor: "npwp" },
    { Header: "Sertifikat", accessor: "sertifikat" },
    {
      Header: "Aksi",
      accessor: "aksi",
      Cell: ({ row }) => (
        <IconButton
          color="info"
          onClick={() => handleEdit(row.original)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  // Data baris, filter sesuai filterJenis dan search
  const filteredRows = perusahaan
    .filter((item) =>
      filterJenis === "All" ? true : item.jenis_perusahaan === filterJenis
    )
    .filter((item) =>
      item.nama_perusahaan.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map((item) => ({
      ...item,
    }));

  const handleOpenDialog = () => {
    setForm({
      nama_perusahaan: "",
      jenis_perusahaan: "",
      alamat: "",
      email: "",
      nomor_telepon: "",
      npwp: "",
      sertifikat: "",
    });
    setIsEdit(false);
    setEditId(null);
    setOpenDialog(true);
  };

  const handleEdit = (data) => {
    setForm({
      nama_perusahaan: data.nama_perusahaan || "",
      jenis_perusahaan: data.jenis_perusahaan || "",
      alamat: data.alamat || "",
      email: data.email || "",
      nomor_telepon: data.nomor_telepon || "",
      npwp: data.npwp || "",
      sertifikat: data.sertifikat || "",
    });
    setIsEdit(true);
    setEditId(data.id_perusahaan);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEdit(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && editId) {
        await axios.put(`${API_HOHOLOK}perusahaan/${editId}`, form);
      } else {
        await axios.post(`${API_HOHOLOK}perusahaan`, form);
      }
      handleCloseDialog();
      fetchPerusahaan();
    } catch (err) {
      alert("Gagal menyimpan data perusahaan");
    }
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        selected={filterJenis === "All"}
        onClick={() => {
          setFilterJenis("All");
          closeMenu();
        }}
      >
        All
      </MenuItem>
      {jenisOptions.map((jenis) => (
        <MenuItem
          key={jenis}
          selected={filterJenis === jenis}
          onClick={() => {
            setFilterJenis(jenis);
            closeMenu();
          }}
        >
          {jenis}
        </MenuItem>
      ))}
    </Menu>
  );

  // Fungsi export CSV
  const handleExportCSV = () => {
    if (!filteredRows.length) return;
    const header = [
      "Nama Perusahaan",
      "Jenis",
      "Alamat",
      "Email",
      "Telepon",
      "NPWP",
      "Sertifikat",
    ];
    const csvRows = [
      header.join(","),
      ...filteredRows.map((row) =>
        [
          row.nama_perusahaan,
          row.jenis_perusahaan,
          row.alamat,
          row.email,
          row.nomor_telepon,
          row.npwp,
          row.sertifikat,
        ]
          .map((v) => `"${(v || "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, "perusahaan.csv");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDButton variant="gradient" color="info" onClick={handleOpenDialog}>
            Tambah Perusahaan
          </MDButton>
          <MDBox display="flex">
            <MDButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              filters&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderMenu}
            <MDBox ml={1}>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleExportCSV}
              >
                <Icon>description</Icon>
                &nbsp;Export CSV
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Card>
          <MDBox p={2} pb={0} display="flex" alignItems="center" gap={2}>
            <TextField
              label="Cari Nama Perusahaan"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 250 }}
            />
          </MDBox>
          <DataTable
            table={{ columns, rows: filteredRows }}
            entriesPerPage={false}
            canSearch
            isLoading={loading}
          />
        </Card>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEdit ? "Edit Perusahaan" : "Tambah Perusahaan"}
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              margin="dense"
              label="Nama Perusahaan"
              name="nama_perusahaan"
              value={form.nama_perusahaan}
              onChange={handleChange}
              fullWidth
              required
            />
            <Autocomplete
              options={jenisOptions}
              value={form.jenis_perusahaan}
              onChange={(e, newValue) =>
                setForm({ ...form, jenis_perusahaan: newValue || "" })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Jenis Perusahaan"
                  margin="dense"
                  required
                />
              )}
              fullWidth
              disableClearable
            />
            <TextField
              margin="dense"
              label="Alamat"
              name="alamat"
              value={form.alamat}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Nomor Telepon"
              name="nomor_telepon"
              value={form.nomor_telepon}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="NPWP"
              name="npwp"
              value={form.npwp}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Sertifikat"
              name="sertifikat"
              value={form.sertifikat}
              onChange={handleChange}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              Simpan
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PerusahaanList;
